import React, { useState, useEffect, useContext } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { UserIcon, LockClosedIcon, ArrowRightIcon } from '@heroicons/react/solid'; // Heroicons v1
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import Logo from '../assets/logo.png'; // Adjust the path if needed
import { AlertContext } from '../contexts/AlertContext';
import { BASE_URL } from '../config';
import axios from 'axios';
import ReCaptchaComponent from '../components/ReCaptchaComponent';
import { useGoogleLogin } from '@react-oauth/google';

const Login_v2 = () => {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    rememberMe: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const [googleUser, setGoogleUser] = useState(null);
  const { showAlert } = useContext(AlertContext);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await axios.post(`${BASE_URL}/login`, {
        username: formData.username,
        password: formData.password,
        rememberMe: formData.rememberMe,
        recaptchaToken,
      });
      const result = response.data;

      if (response.status === 200) {
        showAlert('success', 'Login successful!');
        localStorage.setItem('isLoggedIn', 'true');
        localStorage.setItem('authentication/token', result.access_token);
        localStorage.setItem('authentication/expire', result.expires_in);
        localStorage.setItem('authentication/scopes', result.scope);
        localStorage.setItem('authentication/refresh_token', result.refresh_token);
        localStorage.setItem('authentication/token_type', result.token_type);
        localStorage.setItem('authentication/permissions', JSON.stringify(result.permissions));
        window.location.reload();
      }
    } catch (error) {
      if (error.response) {
        const result = error.response.data;
        if (result.error === 'invalid_recaptcha') {
          showAlert('danger', 'The reCAPTCHA verification failed. Please refresh your browser.');
        } else {
          const errorMessage = typeof result.message === 'object'
            ? Object.values(result.message).flat().join(', ')
            : result.message || 'Invalid credentials';
          showAlert('danger', errorMessage);
        }
      } else {
        showAlert('danger', 'Connection error. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const googleLogin = useGoogleLogin({
    onSuccess: (tokenResponse) => {
      setGoogleUser(tokenResponse);
      setIsLoading(true);
    },
    onError: () => {
      showAlert('danger', 'Google Login failed. Please try again.');
      setIsLoading(false);
    },
  });

  useEffect(() => {
    if (googleUser) {
      const googleAccessToken = googleUser.access_token;
      const handleGoogleLoginSubmit = async () => {
        setIsLoading(true);
        try {
          const response = await axios.post(`${BASE_URL}/google-login`, {
            username: formData.username,
            password: formData.password,
            rememberMe: formData.rememberMe,
            recaptchaToken,
            googleAccessToken,
          });
          const result = response.data;

          if (response.status === 200) {
            showAlert('success', 'Login successful!');
            localStorage.setItem('isLoggedIn', 'true');
            localStorage.setItem('authentication/token', result.access_token);
            localStorage.setItem('authentication/expire', result.expires_in);
            localStorage.setItem('authentication/scopes', result.scope);
            localStorage.setItem('authentication/refresh_token', result.refresh_token);
            localStorage.setItem('authentication/token_type', result.token_type);
            localStorage.setItem('authentication/permissions', JSON.stringify(result.permissions));
            window.location.reload();
          }
        } catch (error) {
          if (error.response) {
            const result = error.response.data;
            const errorMessage = typeof result.message === 'object'
              ? Object.values(result.message).flat().join(', ')
              : result.message || 'Invalid credentials';
            showAlert('danger', errorMessage);
          } else {
            showAlert('danger', 'Connection error. Please try again.');
          }
        } finally {
          setIsLoading(false);
        }
      };
      handleGoogleLoginSubmit();
    }
  }, [googleUser, formData, recaptchaToken]);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.6,
        when: 'beforeChildren',
        staggerChildren: 0.15,
      },
    },
  };

  const childVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 to-gray-300 flex items-center justify-center p-6">
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="w-full max-w-lg bg-white/90 backdrop-blur-xl rounded-2xl shadow-2xl p-8"
      >
        <motion.div variants={childVariants} className="text-center mb-8">
          <img
            src={Logo}
            alt="Billing & Collection Portal Logo"
            className="mx-auto h-20 w-auto mb-4"
          />
          <h1 className="text-3xl font-bold text-gray-900">
            Billing & Collection Portal
          </h1>
          <p className="text-gray-600 mt-2">Sign in to manage your finances</p>
        </motion.div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <motion.div variants={childVariants}>
            <label
              htmlFor="username"
              className="block text-sm font-semibold text-gray-700 mb-1"
            >
              Username or Email
            </label>
            <div className="relative">
              <UserIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-500" />
              <input
                id="username"
                type="text"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                disabled={isLoading}
                placeholder="Enter username or email"
                className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-200 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 disabled:bg-gray-100"
                required
              />
            </div>
          </motion.div>

          <motion.div variants={childVariants}>
            <label
              htmlFor="password"
              className="block text-sm font-semibold text-gray-700 mb-1"
            >
              Password
            </label>
            <div className="relative">
              <LockClosedIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-500" />
              <input
                id="password"
                type="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                disabled={isLoading}
                placeholder="Enter password"
                className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-200 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 disabled:bg-gray-100"
                required
              />
            </div>
          </motion.div>

          <motion.div variants={childVariants} className="flex items-center">
            <input
              id="remember-me"
              type="checkbox"
              name="rememberMe"
              checked={formData.rememberMe}
              onChange={handleInputChange}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label
              htmlFor="remember-me"
              className="ml-2 text-sm text-gray-600"
            >
              Remember me for 2 weeks
            </label>
          </motion.div>

          <motion.button
            variants={childVariants}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            type="submit"
            disabled={isLoading}
            className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 flex items-center justify-center gap-2 shadow-md"
          >
            {isLoading ? (
              <>
                <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                Authenticating...
              </>
            ) : (
              <>
                Sign in
                <ArrowRightIcon className="h-5 w-5" />
              </>
            )}
          </motion.button>
        </form>

        <motion.div
          variants={childVariants}
          className="relative my-6"
        >
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-200"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-3 bg-white text-gray-600">Or continue with</span>
          </div>
        </motion.div>

        <motion.div variants={childVariants}>
          <motion.button
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            onClick={() => googleLogin()}
            disabled={isLoading}
            className="w-full bg-gradient-to-r from-amber-500 to-orange-500 text-white py-3 rounded-lg font-semibold hover:from-amber-600 hover:to-orange-600 transition-all duration-300 flex items-center justify-center gap-2 shadow-md"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24">
              <path
                d="M12.24 10.4v3.2h5.76c-.24 1.2-.96 2.24-2.08 3.04l3.36 2.56c2.08-1.92 3.28-4.8 3.28-8.32 0-.8-.08-1.6-.24-2.4h-10.08z"
                fill="#4285F4"
              />
              <path
                d="M12 24c3.2 0 5.92-1.04 7.92-2.88l-3.36-2.56c-1.04.72-2.4 1.12-4.56 1.12-3.52 0-6.48-2.4-7.52-5.6H1.2v3.52C3.2 21.6 7.36 24 12 24z"
                fill="#34A853"
              />
              <path
                d="M4.48 14.4c-.32-.96-.48-1.92-.48-3.2s.16-2.24.48-3.2V4.48H1.2C.48 6.08 0 7.84 0 10.4s.48 4.32 1.2 5.92l3.28-2.32z"
                fill="#FBBC05"
              />
              <path
                d="M12 4.8c2.24 0 4.24.8 5.76 2.32l4.32-4.32C19.36 1.04 16.64 0 12 0 7.36 0 3.2 2.4 1.2 6.08l3.28 2.32C5.52 7.2 8.48 4.8 12 4.8z"
                fill="#EA4335"
              />
            </svg>
            Login with Google
          </motion.button>
        </motion.div>

        <motion.div variants={childVariants} className="mt-6">
          <ReCaptchaComponent onTokenReceived={setRecaptchaToken} />
        </motion.div>
      </motion.div>
    </div>
  );
};

export default Login_v2;